/**
 * El conflicto de hueco, y qué hay que hacer cuando llega.
 *
 * Vive aquí, y no copiado en cada frontal, porque **es una regla del producto
 * y no un detalle de cada pantalla**: el §9 dice que cuando el hueco elegido se
 * ocupa entre que se pinta la rejilla y se confirma, no se enseña un error
 * genérico — se refresca la rejilla y se pide otra hora.
 *
 * Que estuviera duplicado costó un defecto real: el panel del incremento 19
 * enseñaba el error pero **no refrescaba**, así que seguía ofreciendo el hueco
 * que se acababa de ocupar y el siguiente intento volvía a chocar. El portal lo
 * hacía bien desde el incremento 7. Es exactamente la clase de divergencia que
 * no se ve revisando un fichero, porque el fichero correcto es el otro.
 */

/** El código que devuelve la API cuando el hueco ya no está libre (§8). */
export const CODIGO_HUECO_OCUPADO = 'hueco_ocupado';

/** Lo mínimo que se le pide a un error para poder decidir: lo cumplen los dos frontales. */
export interface FalloDeApi {
  estado: number;
  codigo: string;
}

function tieneFormaDeFallo(fallo: unknown): fallo is FalloDeApi {
  if (typeof fallo !== 'object' || fallo === null) return false;
  const f = fallo as Record<string, unknown>;
  return typeof f['estado'] === 'number' && typeof f['codigo'] === 'string';
}

/**
 * ¿Es este fallo el hueco que se acaba de ocupar?
 *
 * Se comprueban **el estado y el código**, no solo el código: el código por sí
 * solo podría llegar algún día en otra respuesta y esto es una frontera de
 * comportamiento, no un mensaje.
 */
export function esConflictoDeHueco(fallo: unknown): boolean {
  return tieneFormaDeFallo(fallo) && fallo.estado === 409 && fallo.codigo === CODIGO_HUECO_OCUPADO;
}

/**
 * Lo que hay que hacer, sí o sí, ante un conflicto de hueco.
 *
 * Las dos cosas van juntas a propósito. Olvidar el hueco sin refrescar deja al
 * usuario eligiendo sobre una rejilla que miente; refrescar sin olvidar el
 * hueco deja seleccionada una hora que ya no existe.
 */
export interface ReaccionAlConflicto {
  /** Quita el hueco que estaba elegido, que ya no vale. */
  olvidarHueco: () => void;
  /** Vuelve a pedir la rejilla: lo que se está enseñando está obsoleto. */
  refrescarHuecos: () => void;
}

/**
 * Si el fallo es un conflicto de hueco, reacciona y devuelve `true`.
 *
 * Devolver si lo era deja a quien llama decidir lo suyo —un mensaje, volver a
 * un paso anterior— sin tener que repetir la comprobación.
 */
export function alConflictoDeHueco(fallo: unknown, reaccion: ReaccionAlConflicto): boolean {
  if (!esConflictoDeHueco(fallo)) return false;
  reaccion.olvidarHueco();
  reaccion.refrescarHuecos();
  return true;
}
